import {
  Search,
  Store,
  CheckCircle,
  Settings,
  ExternalLink,
  Trash2,
  RefreshCw,
  AlertTriangle,
  X,
  Home,
  Video,
  Zap,
  Bot,
  Bug,
  BarChart,
  Smartphone,
  MessageSquare,
  GitBranch,
  Box,
  Download,
  Cpu,
  Gauge,
  Layout,
  Power,
  FileText,
  Folder,
  Shield,
  Target,
  Sparkles,
  Users,
  Image as ImageIcon,
  Github,
  ClipboardList,
  Lightbulb,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// ────────────────────────────────────────────────────────────
// API client
// ────────────────────────────────────────────────────────────
const API_BASE_URL =
  process.env.REACT_APP_BUGZILLA_BACKEND_URL ||
  "https://www.floxpad.com/bugzilla-api";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access-token");
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (!config.params?.user_email) {
      const email = getUserEmail();
      if (email) config.params = { ...config.params, user_email: email };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("access-token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const getUserEmail = () => {
  let email = localStorage.getItem("user_email");

  if (!email) {
    try {
      const profileDetails = localStorage.getItem("profileDetails");
      if (profileDetails) {
        const parsed = JSON.parse(profileDetails);
        email = parsed.email || parsed.userEmail || null;
      }
    } catch (error) {
      console.error("Error parsing profileDetails:", error);
    }
  }

  if (!email) {
    try {
      const token = localStorage.getItem("access-token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        email = payload.sub || payload.email || null;
      }
    } catch (error) {
      console.error("Error decoding JWT token:", error);
    }
  }

  if (email && email.includes("@") && !localStorage.getItem("user_email")) {
    localStorage.setItem("user_email", email);
  }

  return email;
};

// ────────────────────────────────────────────────────────────
// Category → lucide icon map (so marketplaceTools' string icons
// still render correctly when they're just letters)
// ────────────────────────────────────────────────────────────
const CATEGORY_ICON_MAP = {
  integration: Bug,
  integrations: Bug,
  automation: Cpu,
  "automation engines": Cpu,
  extension: Layout,
  extensions: Layout,
  "desktop tool": Box,
  "desktop tools": Box,
  module: Folder,
  "ai module": Sparkles,
  default: Box,
};

const resolveCategoryIcon = (tool) => {
  if (tool.icon && typeof tool.icon === "function") return tool.icon;
  const key = (tool.tag || tool.type || "").toLowerCase();
  return CATEGORY_ICON_MAP[key] || CATEGORY_ICON_MAP.default;
};

// ────────────────────────────────────────────────────────────
// Tools catalogue — extends the original list with the 7 new
// marketplace tools (RAID, QA, KPI, Test Case Gen, Use Case Gen,
// User Story Gen, Resource Management)
// ────────────────────────────────────────────────────────────
const tools = [
  {
    id: "floxpad-bugzilla",
    name: "Bugzilla",
    desc: "Open source defect tracking system for bug management",
    tag: "Integrations",
    status: "Configure",
    color: "bg-orange-500",
    icon: Bug,
    category: "integration",
    documentation: "https://docs.floxpad.com/integrations/bugzilla",
    version: "1.0.0",
    type: "integration",
  },
  {
    id: "floxpad-pid",
    name: "PID Generator",
    desc: "Project Initiation Document (PID) generator for creating comprehensive project plans, scope definitions, and stakeholder communications.",
    tag: "Integrations",
    status: "Configure",
    color: "bg-indigo-500",
    icon: FileText,
    category: "integration",
    documentation: "https://docs.floxpad.com/integrations/pid",
    version: "1.0.0",
    type: "integration",
  },
  {
    id: "floxpad-automation-engine",
    name: "Floxpad Automation Engine",
    desc: "Test automation engine for intelligent test execution on local system",
    tag: "Automation Engines",
    status: "Download",
    color: "bg-green-500",
    icon: Cpu,
    category: "automation",
    documentation: "https://floxpad.com/docs",
    version: "1.0.0",
    downloadUrl: "https://floxpad.com/downloads/Floxpad_Automation_Engine.zip",
    type: "downloadable",
  },
  {
    id: "floxpad-recording-workflow-ide",
    name: "Recording Workflow IDE",
    desc: "Chrome extension for recording and creating test automation workflows visually",
    tag: "Extensions",
    status: "Install",
    color: "bg-blue-500",
    icon: Layout,
    category: "extension",
    documentation: "https://floxpad.com/docs",
    version: "1.0.0",
    extensionUrl:
      "https://chrome.google.com/webstore/detail/floxpad-recording-workflow",
    type: "extension",
  },

  // ──────────────── NEW TOOLS ────────────────
  {
    id: "floxpad-raid-management",
    name: "Floxpad RAID Management",
    desc: "Centralised RAID log for Risks, Assumptions, Issues & Dependencies with owners, severity scoring, mitigation plans and live roll-ups.",
    tag: "Desktop Tools",
    status: "Configure",
    color: "bg-purple-500",
    icon: Shield,
    category: "module",
    documentation: "https://docs.floxpad.com/modules/raid",
    version: "1.2.0",
    type: "integration",
  },
  {
    id: "floxpad-qa",
    name: "Floxpad QA",
    desc: "End-to-end QA workspace that adapts to your workflow — plan, execute and report on quality with intuitive UX and innovative functionality.",
    tag: "Desktop Tools",
    status: "Configure",
    color: "bg-emerald-500",
    icon: Target,
    category: "module",
    documentation: "https://docs.floxpad.com/modules/qa",
    version: "1.0.0",
    type: "integration",
  },
  {
    id: "floxpad-kpi-governance",
    name: "Floxpad KPI Governance System",
    desc: "Define, measure and govern KPIs across teams with targets, thresholds, RAG status, trend analysis and executive roll-ups.",
    tag: "Desktop Tools",
    status: "Configure",
    color: "bg-indigo-500",
    icon: Gauge,
    category: "module",
    documentation: "https://docs.floxpad.com/modules/kpi",
    version: "1.1.0",
    type: "integration",
  },
  {
    id: "floxpad-testcase-generation",
    name: "Floxpad Test Case Generation",
    desc: "AI-powered test case generation from UI images and GitHub repositories — with edge case detection and review-before-commit.",
    tag: "Automation Engines",
    status: "Configure",
    color: "bg-pink-500",
    icon: ClipboardList,
    category: "ai",
    documentation: "https://docs.floxpad.com/ai/testcase",
    version: "1.0.0",
    type: "integration",
  },
  {
    id: "floxpad-usecase-generation",
    name: "Floxpad Use Case Generation",
    desc: "Generate actor-driven use cases from UI images, diagrams and GitHub repos — with preconditions, main and alternate flows.",
    tag: "Automation Engines",
    status: "Configure",
    color: "bg-teal-500",
    icon: Lightbulb,
    category: "ai",
    documentation: "https://docs.floxpad.com/ai/usecase",
    version: "1.0.0",
    type: "integration",
  },
  {
    id: "floxpad-userstory-generation",
    name: "Floxpad User Story Generation",
    desc: "Convert UI images and GitHub issues/PRs into well-formed user stories with acceptance criteria and story point estimates.",
    tag: "Automation Engines",
    status: "Configure",
    color: "bg-orange-500",
    icon: Users,
    category: "ai",
    documentation: "https://docs.floxpad.com/ai/userstory",
    version: "1.0.0",
    type: "integration",
  },
  {
    id: "floxpad-resource-management",
    name: "Floxpad Resource Management",
    desc: "Plan, allocate and track team resources with capacity planning, allocation heatmaps and utilisation forecasting.",
    tag: "Desktop Tools",
    status: "Configure",
    color: "bg-cyan-500",
    icon: Users,
    category: "module",
    documentation: "https://docs.floxpad.com/modules/resources",
    version: "1.3.0",
    type: "integration",
  },
];

// ────────────────────────────────────────────────────────────
// Dashboard
// ────────────────────────────────────────────────────────────
const MarketplaceDashboard = () => {
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [installedTools, setInstalledTools] = useState({});
  const [bugzillaConfig, setBugzillaConfig] = useState(null);
  const [isBugzillaConfigured, setIsBugzillaConfigured] = useState(false);
  const [isPIDConfigured, setIsPIDConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [showToolDetails, setShowToolDetails] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showBugzillaConfig, setShowBugzillaConfig] = useState(false);
  const [bugzillaUrl, setBugzillaUrl] = useState("");
  const [bugzillaApiKey, setBugzillaApiKey] = useState("");
  const [configLoading, setConfigLoading] = useState(false);
  const [showReconfigureWarning, setShowReconfigureWarning] = useState(false);
  const [showUninstallConfirm, setShowUninstallConfirm] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [toolToAction, setToolToAction] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isLoadingTools, setIsLoadingTools] = useState(false);

  const [stats, setStats] = useState({
    totalInstalled: 0,
    totalIntegrations: 0,
    lastUpdated: null,
  });

  const showToast = (message, type = "success") => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const getAccessToken = () => localStorage.getItem("access-token");

  const updateStats = useCallback(() => {
    const totalInstalled = Object.keys(installedTools).length;
    const totalIntegrations = tools.filter((t) => t.tag === "Integrations").length;
    setStats({
      totalInstalled,
      totalIntegrations,
      lastUpdated: new Date().toISOString(),
    });
  }, [installedTools]);

  // ── PID config check ──────────────────────────────────────
  const checkPIDConfiguration = useCallback(() => {
    const userEmail = getUserEmail();
    if (!userEmail) return false;

    const configured =
      localStorage.getItem(`pid_configured_${userEmail}`) === "true" ||
      localStorage.getItem("pid_configured") === "true";

    const pidConfigStr =
      localStorage.getItem(`pidConfig_${userEmail}`) ||
      localStorage.getItem("pidConfig");

    let isEnabled = configured;
    if (pidConfigStr) {
      try {
        const config = JSON.parse(pidConfigStr);
        isEnabled = configured && config.enabled !== false;
      } catch (e) {
        isEnabled = configured;
      }
    }

    setIsPIDConfigured(isEnabled);
    return isEnabled;
  }, []);

  // ── Load installed tools ──────────────────────────────────
  const loadInstalledTools = useCallback(async () => {
    if (isLoadingTools) return;

    setIsLoadingTools(true);
    setLoading(true);

    try {
      const token = getAccessToken();
      const userEmail = getUserEmail();

      if (!token || !userEmail) {
        setLoading(false);
        setIsLoadingTools(false);
        return;
      }

      const newInstalledTools = {};

      const bugzillaConfigStr =
        localStorage.getItem(`bugzillaConfig_${userEmail}`) ||
        localStorage.getItem("bugzillaConfig") ||
        localStorage.getItem("bugzilla_config");

      const bugzillaConfigured =
        localStorage.getItem(`bugzilla_configured_${userEmail}`) === "true" ||
        localStorage.getItem("bugzilla_configured") === "true";

      if (bugzillaConfigStr && bugzillaConfigured) {
        try {
          const config = JSON.parse(bugzillaConfigStr);
          newInstalledTools["floxpad-bugzilla"] = {
            ...config,
            isInstalled: true,
            isEnabled: config.isEnabled !== false,
            id: "floxpad-bugzilla",
            name: "Bugzilla",
            type: "bugzilla",
            integration_id:
              config.integrationId ||
              config.integration_id ||
              config.id ||
              "bugzilla-integration",
          };
          setIsBugzillaConfigured(true);
          setBugzillaConfig(config);
        } catch (e) {
          console.error("Error parsing Bugzilla config:", e);
        }
      }

      const pidConfigStr =
        localStorage.getItem(`pidConfig_${userEmail}`) ||
        localStorage.getItem("pidConfig");
      const pidConfigured =
        localStorage.getItem(`pid_configured_${userEmail}`) === "true" ||
        localStorage.getItem("pid_configured") === "true";

      if (pidConfigStr && pidConfigured) {
        try {
          const config = JSON.parse(pidConfigStr);
          newInstalledTools["floxpad-pid"] = {
            ...config,
            isInstalled: true,
            isEnabled: config.enabled !== false,
            id: "floxpad-pid",
            name: "PID Generator",
            type: "pid",
            integration_id:
              config.integrationId || config.integration_id || "pid-integration",
          };
          setIsPIDConfigured(config.enabled !== false);
        } catch (e) {
          console.error("Error parsing PID config:", e);
        }
      }

      try {
        const response = await apiClient.get("/integrations/list", {
          params: { user_email: userEmail },
          timeout: 5000,
        });

        if (response.data.success && response.data.data) {
          const userIntegrations = response.data.data.filter(
            (integration) => integration.user_email === userEmail
          );

          userIntegrations.forEach((integration) => {
            const toolId = `floxpad-${integration.type || "bugzilla"}`;
            newInstalledTools[toolId] = {
              ...integration,
              isInstalled: true,
              integration_id: integration.id,
              isEnabled: integration.is_enabled !== false,
              id: toolId,
              name:
                integration.type === "bugzilla"
                  ? "Bugzilla"
                  : integration.type === "pid"
                  ? "PID Generator"
                  : integration.type,
              type: integration.type || "bugzilla",
            };

            if (
              integration.type === "bugzilla" ||
              toolId === "floxpad-bugzilla"
            ) {
              setIsBugzillaConfigured(true);
              setBugzillaConfig({
                url: integration.bugzilla_url,
                apiKey: "********",
                isInstalled: true,
                integrationId: integration.id,
                installedAt: integration.created_at,
                isEnabled: integration.is_enabled !== false,
              });
            }

            if (integration.type === "pid" || toolId === "floxpad-pid") {
              setIsPIDConfigured(integration.is_enabled !== false);
            }
          });
        }
      } catch (apiError) {
        console.warn(
          "API call failed, using localStorage only:",
          apiError.message
        );
      }

      setInstalledTools(newInstalledTools);
      updateStats();
    } catch (error) {
      console.error("Error loading installed tools:", error);
    } finally {
      setLoading(false);
      setIsLoadingTools(false);
    }
  }, [updateStats, isLoadingTools]);

  const refreshTools = useCallback(() => {
    if (!isLoadingTools) loadInstalledTools();
  }, [loadInstalledTools, isLoadingTools]);

  useEffect(() => {
    const handleBugzillaUpdate = () => {
      setTimeout(() => refreshTools(), 200);
    };
    window.addEventListener("bugzilla-config-updated", handleBugzillaUpdate);
    return () =>
      window.removeEventListener(
        "bugzilla-config-updated",
        handleBugzillaUpdate
      );
  }, [refreshTools]);

  useEffect(() => {
    const initializeTools = async () => {
      const userEmail = getUserEmail();
      if (userEmail) await loadInstalledTools();
    };
    initializeTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateStats();
  }, [installedTools, updateStats]);

  // ── Filtering ─────────────────────────────────────────────
  const filteredTools = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return tools.filter((tool) => {
      const matchesFilter =
        activeFilter === "All" || tool.tag === activeFilter;
      const matchesSearch =
        tool.name.toLowerCase().includes(q) ||
        tool.desc.toLowerCase().includes(q);
      return matchesFilter && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  // ── Handlers ──────────────────────────────────────────────
  const handleToolClick = (tool) => {
    setSelectedTool(tool);
    setShowToolDetails(true);
  };

  const handleDownload = (tool) => {
    if (!tool.downloadUrl) return;
    setDownloading(true);
    showToast(`Starting download of ${tool.name}...`, "success");

    const link = document.createElement("a");
    link.href = tool.downloadUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setDownloading(false), 2000);
  };

  const openBugzillaConfig = () => {
    const userEmail = getUserEmail();
    if (!userEmail) {
      showToast("Please login first to configure Bugzilla", "warning");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    const storedConfig =
      localStorage.getItem(`bugzillaConfig_${userEmail}`) ||
      localStorage.getItem("bugzillaConfig") ||
      localStorage.getItem("bugzilla_config");

    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig);
        setBugzillaUrl(config.url || config.bugzilla_url || "");
        setBugzillaApiKey(config.apiKey || "");
      } catch (e) {
        setBugzillaUrl("");
        setBugzillaApiKey("");
      }
    } else {
      setBugzillaUrl("");
      setBugzillaApiKey("");
    }

    setShowBugzillaConfig(true);
  };

  const handleBugzillaConfigSubmit = async () => {
    if (!bugzillaUrl || !bugzillaApiKey) {
      showToast("Please fill in both URL and API Key", "warning");
      return;
    }

    try {
      new URL(bugzillaUrl);
    } catch (e) {
      showToast(
        "Please enter a valid URL (e.g., https://bugzilla.example.com)",
        "warning"
      );
      return;
    }

    const token = getAccessToken();
    const userEmail = getUserEmail();

    if (!token || !userEmail) {
      showToast("Session expired. Please login again.", "error");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    if (!userEmail.includes("@")) {
      showToast("Invalid user email format. Please login again.", "error");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    setConfigLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/integrations/install?user_email=${encodeURIComponent(
          userEmail
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            bugzilla_url: bugzillaUrl,
            api_key: bugzillaApiKey,
            type: "bugzilla",
            is_enabled: true,
            user_email: userEmail,
            settings: {
              auto_create_on_failure: true,
              notify_on_update: true,
              sync_comments: true,
            },
          }),
        }
      );

      const responseText = await response.text();
      let responseData;

      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        if (
          responseText.includes('"success":true') ||
          responseText.includes("Integration installed successfully") ||
          responseText.includes("success")
        ) {
          const idMatch = responseText.match(/"integration_id":(\d+)/);
          const integrationId = idMatch ? parseInt(idMatch[1]) : 1;
          responseData = {
            success: true,
            data: { integration_id: integrationId },
            message: "Integration installed successfully",
          };
        } else {
          throw new Error(
            `Unexpected response: ${responseText.substring(0, 200)}`
          );
        }
      }

      if (responseData.success) {
        const integrationId =
          responseData.data?.integration_id ||
          responseData.data?.id ||
          (responseText.match(/"integration_id":(\d+)/)
            ? parseInt(responseText.match(/"integration_id":(\d+)/)[1])
            : 1);

        const finalConfig = {
          url: bugzillaUrl,
          apiKey: bugzillaApiKey,
          isInstalled: true,
          integration_id: integrationId,
          installedAt:
            responseData.data?.installed_at || new Date().toISOString(),
          configuredAt: new Date().toISOString(),
          user_email: userEmail,
          isEnabled: true,
        };

        localStorage.setItem(
          `bugzillaConfig_${userEmail}`,
          JSON.stringify(finalConfig)
        );
        localStorage.setItem(`bugzilla_configured_${userEmail}`, "true");
        localStorage.setItem("bugzillaConfig", JSON.stringify(finalConfig));
        localStorage.setItem("bugzilla_configured", "true");
        localStorage.setItem("bugzilla_config", JSON.stringify(finalConfig));
        localStorage.setItem("bugzilla_url", bugzillaUrl);
        localStorage.setItem("bugzilla_api_key", bugzillaApiKey);
        localStorage.setItem("bugzilla_integration_id", integrationId);
        localStorage.setItem(
          "bugzilla_configured_at",
          new Date().toISOString()
        );

        setIsBugzillaConfigured(true);
        setBugzillaConfig(finalConfig);

        setInstalledTools((prev) => ({
          ...prev,
          "floxpad-bugzilla": {
            ...finalConfig,
            id: "floxpad-bugzilla",
            name: "Bugzilla",
            type: "bugzilla",
            isInstalled: true,
            integration_id: integrationId,
          },
        }));

        window.dispatchEvent(
          new CustomEvent("bugzilla-config-updated", {
            detail: { configured: true, config: finalConfig },
          })
        );

        showToast("Bugzilla configured successfully!", "success");
        setShowBugzillaConfig(false);
        setTimeout(() => refreshTools(), 500);
      } else {
        showToast(responseData.error || "Failed to configure Bugzilla", "error");
      }
    } catch (error) {
      console.error("Error configuring Bugzilla:", error);
      showToast("Failed to configure Bugzilla. Please try again.", "error");
    } finally {
      setConfigLoading(false);
    }
  };

  const handlePIDConfigure = async () => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      showToast("Please login first to configure PID Generator", "warning");
      setTimeout(() => navigate("/login"), 1500);
      return false;
    }

    try {
      const config = {
        projectPrefix: "PID",
        templateVersion: "v2.0",
        defaultTemplate: "standard",
        autoGenerate: true,
        enableNotifications: true,
        enabled: true,
      };

      const finalConfig = {
        ...config,
        configuredAt: new Date().toISOString(),
        isConfigured: true,
        user_email: userEmail,
        isEnabled: true,
        integration_id: "pid-integration",
      };

      localStorage.setItem(
        `pidConfig_${userEmail}`,
        JSON.stringify(finalConfig)
      );
      localStorage.setItem(`pid_configured_${userEmail}`, "true");
      localStorage.setItem("pid_configured", "true");
      localStorage.setItem("pidConfig", JSON.stringify(finalConfig));

      setIsPIDConfigured(true);

      setInstalledTools((prev) => ({
        ...prev,
        "floxpad-pid": {
          ...finalConfig,
          isInstalled: true,
          isEnabled: config.enabled !== false,
          id: "floxpad-pid",
          name: "PID Generator",
          type: "pid",
        },
      }));

      window.dispatchEvent(new CustomEvent("pid-config-updated"));
      showToast("PID Generator configured successfully!", "success");
      refreshTools();
      return true;
    } catch (error) {
      console.error("Error configuring PID:", error);
      showToast("Failed to configure PID Generator", "error");
      return false;
    }
  };

  const handleInstallClick = (tool) => {
    if (tool.id === "floxpad-bugzilla") return openBugzillaConfig();
    if (tool.id === "floxpad-pid") return handlePIDConfigure();

    if (tool.type === "extension" && tool.extensionUrl) {
      window.open(tool.extensionUrl, "_blank");
      showToast(`Opening ${tool.name} in Chrome Web Store...`, "success");
      return;
    }

    if (tool.type === "downloadable" && tool.downloadUrl) {
      handleDownload(tool);
      return;
    }

    navigate(`/marketplace/${tool.id}`);
  };

  const handleRemoveClick = (tool) => {
    if (!isToolInstalled(tool.id)) {
      showToast(`${tool.name} is not installed.`, "warning");
      return;
    }
    setToolToAction(tool);
    setActionType("uninstall");
    setShowUninstallConfirm(true);
  };

  const handleReconfigureClick = (tool) => {
    if (!isToolInstalled(tool.id)) {
      showToast(
        `${tool.name} is not installed. Please configure it first.`,
        "warning"
      );
      return;
    }
    if (tool.id === "floxpad-bugzilla" || tool.id === "floxpad-pid") {
      setToolToAction(tool);
      setActionType("reconfigure");
      setShowReconfigureWarning(true);
    }
  };

  const handleDisableClick = (tool) => {
    if (!isToolInstalled(tool.id)) {
      showToast(`${tool.name} is not installed.`, "warning");
      return;
    }
    setToolToAction(tool);
    setActionType("disable");
    setShowDisableConfirm(true);
  };

  const confirmReconfigure = () => {
    setShowReconfigureWarning(false);
    if (toolToAction?.id === "floxpad-bugzilla") openBugzillaConfig();
    else if (toolToAction?.id === "floxpad-pid") handlePIDConfigure();
    setToolToAction(null);
    setActionType(null);
  };

  const cancelReconfigure = () => {
    setShowReconfigureWarning(false);
    setToolToAction(null);
    setActionType(null);
  };

  const confirmDisable = async () => {
    setShowDisableConfirm(false);
    const tool = toolToAction;
    setToolToAction(null);
    setActionType(null);

    const token = getAccessToken();
    const userEmail = getUserEmail();

    if (!token || !userEmail) {
      showToast("No session found. Please login again.", "error");
      return;
    }

    setIsDisabling(true);

    try {
      const currentStatus = installedTools[tool.id]?.isEnabled !== false;
      const newStatus = !currentStatus;
      const integrationId =
        installedTools[tool.id]?.integration_id || installedTools[tool.id]?.id;

      if (!integrationId) {
        showToast(
          "Integration ID not found. Please reconfigure the integration.",
          "error"
        );
        setIsDisabling(false);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/integrations/${integrationId}/status?user_email=${encodeURIComponent(
          userEmail
        )}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ is_enabled: newStatus, user_email: userEmail }),
        }
      );

      const responseData = await response.json();

      if (responseData.success) {
        setInstalledTools((prev) => {
          const newState = { ...prev };
          if (newState[tool.id]) {
            newState[tool.id] = { ...newState[tool.id], isEnabled: newStatus };
          }
          return newState;
        });

        if (tool.id === "floxpad-bugzilla") {
          setIsBugzillaConfigured(newStatus);
          setBugzillaConfig((prev) => ({ ...prev, isEnabled: newStatus }));
          localStorage.setItem(
            `bugzilla_configured_${userEmail}`,
            newStatus ? "true" : "false"
          );
          localStorage.setItem(
            "bugzilla_configured",
            newStatus ? "true" : "false"
          );
          if (bugzillaConfig) {
            localStorage.setItem(
              `bugzillaConfig_${userEmail}`,
              JSON.stringify({ ...bugzillaConfig, isEnabled: newStatus })
            );
          }
        }

        showToast(
          `${tool.name} has been ${
            newStatus ? "enabled" : "disabled"
          } successfully!`,
          "success"
        );
        refreshTools();
      } else {
        showToast(
          responseData.error || "Failed to update integration status",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating integration status:", error);
      showToast("Error updating integration status", "error");
    } finally {
      setIsDisabling(false);
    }
  };

  const cancelDisable = () => {
    setShowDisableConfirm(false);
    setToolToAction(null);
    setActionType(null);
  };

  const confirmUninstall = async () => {
    setShowUninstallConfirm(false);
    const tool = toolToAction;
    setToolToAction(null);
    setActionType(null);

    if (tool?.id === "floxpad-pid") {
      setLoading(true);
      try {
        const userEmail = getUserEmail();
        [
          "pidConfig",
          "pid_configured",
          `pidConfig_${userEmail}`,
          `pid_configured_${userEmail}`,
        ].forEach((key) => localStorage.removeItem(key));
        setIsPIDConfigured(false);

        setInstalledTools((prev) => {
          const newState = { ...prev };
          delete newState[tool.id];
          return newState;
        });

        window.dispatchEvent(new CustomEvent("pid-config-updated"));
        showToast(`${tool.name} has been successfully uninstalled!`, "success");
        refreshTools();
      } catch (error) {
        console.error("Error uninstalling PID:", error);
        showToast("Failed to uninstall PID Generator", "error");
      } finally {
        setLoading(false);
      }
      return;
    }

    const token = getAccessToken();
    const userEmail = getUserEmail();

    if (!token || !userEmail) {
      showToast("No session found. Please login again.", "error");
      return;
    }

    setLoading(true);

    try {
      const integrationId =
        installedTools[tool.id]?.integration_id || installedTools[tool.id]?.id;

      let response;
      if (integrationId) {
        response = await fetch(
          `${API_BASE_URL}/integrations/${integrationId}?user_email=${encodeURIComponent(
            userEmail
          )}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        response = await fetch(
          `${API_BASE_URL}/integrations/bugzilla/data?user_email=${encodeURIComponent(
            userEmail
          )}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      const responseData = await response.json();

      if (responseData.success) {
        if (tool.id === "floxpad-bugzilla") {
          [
            "bugzillaConfig",
            "bugzilla_configured",
            "bugzilla_configured_at",
            "bugzilla_url",
            "bugzilla_api_key",
            "bugzilla_integration_id",
            "bugzilla_config",
            `bugzillaConfig_${userEmail}`,
            `bugzilla_configured_${userEmail}`,
          ].forEach((key) => localStorage.removeItem(key));
          setIsBugzillaConfigured(false);
          setBugzillaConfig(null);

          window.dispatchEvent(
            new CustomEvent("bugzilla-config-updated", {
              detail: { configured: false },
            })
          );
        }

        setInstalledTools((prev) => {
          const newState = { ...prev };
          delete newState[tool.id];
          return newState;
        });

        showToast(`${tool.name} has been successfully uninstalled!`, "success");
        refreshTools();
      } else {
        showToast(responseData.error || "Failed to uninstall", "error");
      }
    } catch (error) {
      console.error("Error removing integration:", error);
      showToast("Error during uninstall", "error");
    } finally {
      setLoading(false);
    }
  };

  const cancelUninstall = () => {
    setShowUninstallConfirm(false);
    setToolToAction(null);
    setActionType(null);
  };

  const isToolInstalled = useCallback(
    (toolId) => {
      if (toolId === "floxpad-bugzilla") {
        const userEmail = getUserEmail();
        const stateCheck = isBugzillaConfigured === true;
        const toolsCheck =
          installedTools[toolId] !== undefined &&
          installedTools[toolId] !== null &&
          installedTools[toolId].isInstalled === true;
        const localStorageCheck =
          localStorage.getItem(`bugzilla_configured_${userEmail}`) === "true" ||
          localStorage.getItem("bugzilla_configured") === "true";
        const configCheck =
          localStorage.getItem(`bugzillaConfig_${userEmail}`) !== null ||
          localStorage.getItem("bugzillaConfig") !== null ||
          localStorage.getItem("bugzilla_config") !== null;
        return stateCheck || toolsCheck || localStorageCheck || configCheck;
      }

      if (toolId === "floxpad-pid") {
        const userEmail = getUserEmail();
        return (
          isPIDConfigured === true ||
          (installedTools[toolId] !== undefined &&
            installedTools[toolId] !== null) ||
          localStorage.getItem(`pid_configured_${userEmail}`) === "true" ||
          localStorage.getItem("pid_configured") === "true"
        );
      }

      return (
        installedTools[toolId] !== undefined &&
        installedTools[toolId] !== null &&
        installedTools[toolId].isInstalled === true
      );
    },
    [isBugzillaConfigured, installedTools, isPIDConfigured]
  );

  const isToolEnabled = (toolId) => {
    const tool = installedTools[toolId];
    if (!tool) return false;
    return tool.isEnabled !== false;
  };

  const categories = [
    "All",
    "Extensions",
    "Desktop Tools",
    "Automation Engines",
    "Integrations",
  ];

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="h-screen overflow-y-auto bg-gradient-to-b from-background to-secondary/20">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-20 right-4 z-50 animate-in slide-in-from-right-5 duration-300">
          <div
            className={`rounded-lg shadow-lg p-4 flex items-center gap-3 ${
              toastMessage.type === "success"
                ? "bg-green-500 text-white"
                : toastMessage.type === "warning"
                ? "bg-yellow-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toastMessage.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <AlertTriangle size={18} />
            )}
            <span>{toastMessage.message}</span>
          </div>
        </div>
      )}

      {/* Bugzilla config modal */}
      {showBugzillaConfig && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowBugzillaConfig(false)}
        >
          <div
            className="bg-background rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-border/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Bug className="text-orange-500" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Configure Bugzilla</h2>
                  <p className="text-sm text-muted-foreground">
                    Enter your Bugzilla credentials
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBugzillaConfig(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Bugzilla URL
                </label>
                <input
                  type="url"
                  value={bugzillaUrl}
                  onChange={(e) => setBugzillaUrl(e.target.value)}
                  placeholder="https://bugzilla.example.com"
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the full URL of your Bugzilla instance
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">API Key</label>
                <input
                  type="password"
                  value={bugzillaApiKey}
                  onChange={(e) => setBugzillaApiKey(e.target.value)}
                  placeholder="Enter your Bugzilla API key"
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Generate an API key from your Bugzilla user preferences
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-2">
                <p className="text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>
                    API keys are stored securely and used to authenticate with
                    your Bugzilla instance.
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleBugzillaConfigSubmit}
                disabled={configLoading}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {configLoading ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Connect to Bugzilla
                  </>
                )}
              </button>
              <button
                onClick={() => setShowBugzillaConfig(false)}
                className="px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable / Enable confirm */}
      {showDisableConfirm && toolToAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={cancelDisable}
        >
          <div
            className="bg-background rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    installedTools[toolToAction.id]?.isEnabled !== false
                      ? "bg-yellow-500/20"
                      : "bg-green-500/20"
                  }`}
                >
                  <Power
                    className={
                      installedTools[toolToAction.id]?.isEnabled !== false
                        ? "text-yellow-500"
                        : "text-green-500"
                    }
                    size={24}
                  />
                </div>
                <h2 className="text-xl font-bold">
                  {installedTools[toolToAction.id]?.isEnabled !== false
                    ? "Disable"
                    : "Enable"}{" "}
                  Integration
                </h2>
              </div>
              <button
                onClick={cancelDisable}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-muted-foreground mb-4">
              Are you sure you want to{" "}
              <strong>
                {installedTools[toolToAction.id]?.isEnabled !== false
                  ? "disable"
                  : "enable"}
              </strong>{" "}
              <strong>{toolToAction.name}</strong>?
            </p>

            <div className="flex gap-3">
              <button
                onClick={confirmDisable}
                className={`flex-1 px-4 py-2 text-white rounded-lg font-medium transition-all ${
                  installedTools[toolToAction.id]?.isEnabled !== false
                    ? "bg-yellow-500 hover:bg-yellow-600"
                    : "bg-green-500 hover:bg-green-600"
                }`}
                disabled={isDisabling}
              >
                {isDisabling ? (
                  <>
                    <RefreshCw size={16} className="animate-spin inline mr-2" />
                    Processing...
                  </>
                ) : installedTools[toolToAction.id]?.isEnabled !== false ? (
                  "Yes, Disable"
                ) : (
                  "Yes, Enable"
                )}
              </button>
              <button
                onClick={cancelDisable}
                className="flex-1 px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconfigure warning */}
      {showReconfigureWarning && toolToAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={cancelReconfigure}
        >
          <div
            className="bg-background rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="text-yellow-500" size={24} />
                </div>
                <h2 className="text-xl font-bold">Reconfigure Integration</h2>
              </div>
              <button
                onClick={cancelReconfigure}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-muted-foreground mb-4">
              You are about to reconfigure <strong>{toolToAction.name}</strong>.
            </p>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-600 dark:text-yellow-400 flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  Reconfiguring will replace your current configuration with
                  default settings.
                </span>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={confirmReconfigure}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-all"
              >
                Continue
              </button>
              <button
                onClick={cancelReconfigure}
                className="flex-1 px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Home button */}
      <button
        onClick={() => navigate("/")}
        className="fixed top-24 left-6 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground border border-cyan-400/30 flex items-center justify-center shadow-lg shadow-cyan-500/20 backdrop-blur-md hover:scale-110 hover:shadow-cyan-400/40 hover:border-cyan-300/50 transition-all duration-300 group"
      >
        <Home size={20} className="group-hover:rotate-12 transition-transform" />
      </button>

      {/* Hero */}
      <section className="relative">
        <div className="relative overflow-hidden px-4 pb-24">
          <div className="absolute inset-0 bg-hero-gradient" />
          <div className="absolute inset-0 grid-pattern opacity-30" />
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-emerald/20 rounded-full blur-[180px]" />
          <div className="absolute top-[10%] left-[10%] w-[500px] h-[500px] bg-cyan/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-[5%] w-[500px] h-[500px] bg-emerald/10 rounded-full blur-[150px]" />

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center rounded-2xl py-20">
              <div className="text-white max-w-6xl mx-auto space-y-4 px-4 text-center">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-pulse">
                  <Store size={16} />
                  Floxpad Marketplace
                </span>

                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-black dark:text-white leading-tight mb-6">
                  Extend Floxpad with the <br />
                  <span className="text-gradient bg-gradient-to-r from-primary to-cyan-500 bg-clip-text text-transparent">
                    tools your QA team needs
                  </span>
                </h1>

                <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
                  Discover, install and connect recorders, runners, automation
                  engines and integrations — all in one place.
                </p>

                <div className="flex justify-center gap-8 mt-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {tools.length}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Available Tools
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-500">
                      {Object.keys(installedTools).length}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Installed
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-500">
                      {stats.totalIntegrations}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Integrations
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Grid + filters */}
        <div className="bg-background px-4 pt-8 pb-16 rounded-t-3xl shadow-2xl">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-8">
              <div className="relative w-full md:w-96">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                />
              </div>

              <div className="flex gap-2 flex-wrap">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveFilter(category)}
                    className={`px-4 py-1.5 text-sm rounded-full border transition-all duration-200 ${
                      activeFilter === category
                        ? "bg-primary text-white dark:text-black border-primary shadow-lg scale-105"
                        : "bg-background border-border hover:bg-muted hover:scale-105"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : filteredTools.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground">
                  No tools found matching your criteria
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredTools.map((tool) => {
                  const isInstalled = isToolInstalled(tool.id);
                  const isEnabled = isToolEnabled(tool.id);
                  const IconComponent = resolveCategoryIcon(tool);
                  const isDownloadable = tool.type === "downloadable";
                  const isExtension = tool.type === "extension";
                  const isIntegration = tool.type === "integration";
                  const isPID = tool.id === "floxpad-pid";

                  return (
                    <div
                      key={tool.id}
                      className={`group relative border rounded-xl p-5 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer bg-gradient-to-br from-background to-secondary/5 flex flex-col h-full ${
                        isInstalled && !isEnabled && !isPID
                          ? "border-yellow-500/50"
                          : "border-border"
                      } hover:border-primary/40`}
                      onClick={() => handleToolClick(tool)}
                    >
                      <div
                        className={`w-12 h-12 flex items-center justify-center text-white rounded-xl mb-4 ${tool.color} shadow-lg group-hover:scale-110 transition-transform ${
                          isInstalled && !isEnabled && !isPID ? "opacity-50" : ""
                        }`}
                      >
                        <IconComponent size={24} />
                      </div>

                      <div className="space-y-2 flex-1">
                        <h4 className="font-semibold text-medium flex items-center gap-2">
                          {tool.name}
                          {isInstalled && !isEnabled && !isPID && (
                            <span className="text-xs bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded-full">
                              Disabled
                            </span>
                          )}
                        </h4>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {tool.desc}
                        </p>

                        <div className="flex gap-2 pt-2 flex-wrap"> 
                          <span className="text-xs px-2 py-1 rounded-full bg-muted">
                            {tool.tag}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            v{tool.version}
                          </span>
                        </div>

                        <div className="flex gap-2 pt-3">
                          {isInstalled ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveClick(tool);
                                }}
                                className="flex-1 text-xs px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 hover:scale-105 transition-all flex items-center justify-center gap-2"
                              >
                                <Trash2 size={14} />
                                Remove
                              </button>
                              {!isPID && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDisableClick(tool);
                                  }}
                                  className={`flex-1 text-xs px-3 py-1.5 rounded-lg transition-all flex items-center justify-center gap-2 ${
                                    isEnabled
                                      ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                      : "bg-green-500 text-white hover:bg-green-600"
                                  }`}
                                >
                                  <Power size={14} />
                                  {isEnabled ? "Disable" : "Enable"}
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInstallClick(tool);
                              }}
                              className="w-full text-xs px-3 py-1.5 rounded-lg bg-primary text-white dark:text-black hover:scale-105 hover:shadow-lg transition-all flex items-center justify-center gap-2"
                              disabled={downloading && isDownloadable}
                            >
                              {isDownloadable ? (
                                downloading ? (
                                  <>
                                    <RefreshCw
                                      size={12}
                                      className="animate-spin"
                                    />
                                    Downloading...
                                  </>
                                ) : (
                                  <>
                                    <Download size={12} />
                                    Download
                                  </>
                                )
                              ) : isExtension ? (
                                <>
                                  <ExternalLink size={12} />
                                  Install Extension
                                </>
                              ) : isIntegration ? (
                                "Configure"
                              ) : (
                                "Install"
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      <div
                        className={`absolute inset-0 rounded-xl border-2 transition-all pointer-events-none ${
                          isInstalled && !isEnabled && !isPID
                            ? "border-yellow-500/30 group-hover:border-yellow-500/50"
                            : "border-transparent group-hover:border-primary/30"
                        }`}
                      ></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Uninstall confirm */}
      {showUninstallConfirm && toolToAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={cancelUninstall}
        >
          <div
            className="bg-background rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="text-red-500" size={24} />
                </div>
                <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
                  Remove {toolToAction.name}
                </h2>
              </div>
              <button
                onClick={cancelUninstall}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-muted-foreground mb-4">
              Are you sure you want to remove{" "}
              <strong>{toolToAction.name}</strong>?
            </p>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400 font-semibold mb-2">
                This will permanently delete:
              </p>
              <ul className="text-sm text-red-600/80 dark:text-red-400/80 space-y-1 ml-4 list-disc">
                <li>All configuration data</li>
                <li>All synced bugs and defects</li>
                <li>Integration logs and history</li>
              </ul>
            </div>

            <p className="text-xs text-muted-foreground mb-6">
              This action <strong>cannot be undone</strong>. All data will be
              permanently lost.
            </p>

            <div className="flex gap-3">
              <button
                onClick={confirmUninstall}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all"
              >
                Yes, Remove
              </button>
              <button
                onClick={cancelUninstall}
                className="flex-1 px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tool details modal */}
      {showToolDetails && selectedTool && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowToolDetails(false)}
        >
          <div
            className="bg-background rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`${selectedTool.color} h-32 rounded-t-2xl flex items-center justify-center relative`}
            >
              {(() => {
                const IconComponent = resolveCategoryIcon(selectedTool);
                return <IconComponent size={48} className="text-white" />;
              })()}
              <button
                onClick={() => setShowToolDetails(false)}
                className="absolute top-4 right-4 p-1 bg-black/20 hover:bg-black/30 rounded-lg transition-colors text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">{selectedTool.name}</h2>
              <p className="text-muted-foreground mb-4">{selectedTool.desc}</p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Category:</span>
                  <span className="text-sm px-2 py-1 rounded-full bg-muted">
                    {selectedTool.tag}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Version:</span>
                  <span className="text-sm">{selectedTool.version}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Type:</span>
                  <span className="text-sm px-2 py-1 rounded-full bg-primary/10 text-primary">
                    {selectedTool.type === "downloadable"
                      ? "Downloadable Tool"
                      : selectedTool.type === "extension"
                      ? "Chrome Extension"
                      : "Integration"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Status:</span>
                  <span
                    className={`text-sm ${
                      isToolInstalled(selectedTool.id)
                        ? isToolEnabled(selectedTool.id)
                          ? "text-green-500"
                          : "text-yellow-500"
                        : "text-primary"
                    }`}
                  >
                    {isToolInstalled(selectedTool.id)
                      ? isToolEnabled(selectedTool.id)
                        ? "Configured ✓"
                        : "Disabled"
                      : "Ready to install"}
                  </span>
                </div>
                {selectedTool.downloadUrl && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Download:</span>
                    <a
                      href={selectedTool.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <Download size={14} />
                      Download ZIP
                    </a>
                  </div>
                )}
                {selectedTool.extensionUrl && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Extension:</span>
                    <a
                      href={selectedTool.extensionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={14} />
                      Chrome Web Store
                    </a>
                  </div>
                )}
              </div>

              <div className="flex gap-3 flex-wrap">
                {isToolInstalled(selectedTool.id) ? (
                  <>
                    <button
                      onClick={() => {
                        handleRemoveClick(selectedTool);
                        setShowToolDetails(false);
                      }}
                      className="flex-1 py-2 rounded-lg font-medium transition-all bg-red-500 text-white hover:bg-red-600 hover:scale-105 flex items-center justify-center gap-2"
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                    {selectedTool.id !== "floxpad-pid" && (
                      <>
                        <button
                          onClick={() => {
                            handleReconfigureClick(selectedTool);
                            setShowToolDetails(false);
                          }}
                          className="flex-1 py-2 rounded-lg font-medium transition-all bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 flex items-center justify-center gap-2"
                        >
                          <Settings size={16} />
                          Reconfigure
                        </button>
                        <button
                          onClick={() => {
                            handleDisableClick(selectedTool);
                            setShowToolDetails(false);
                          }}
                          className={`flex-1 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                            isToolEnabled(selectedTool.id)
                              ? "bg-yellow-500 text-white hover:bg-yellow-600"
                              : "bg-green-500 text-white hover:bg-green-600"
                          }`}
                        >
                          <Power size={16} />
                          {isToolEnabled(selectedTool.id) ? "Disable" : "Enable"}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => {
                      handleInstallClick(selectedTool);
                      setShowToolDetails(false);
                    }}
                    className="flex-1 py-2 rounded-lg font-medium transition-all bg-primary text-white hover:scale-105"
                  >
                    {selectedTool.type === "downloadable"
                      ? "Download Now"
                      : selectedTool.type === "extension"
                      ? "Install Extension"
                      : "Configure Now"}
                  </button>
                )}

                <a
                  href={selectedTool.documentation}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-all"
                >
                  <ExternalLink size={16} />
                  Documentation
                </a>

                <button
                  onClick={() => setShowToolDetails(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceDashboard;